import { ICenter } from './center';
import { IDonor } from './donor';
import { IInsuranceDetails } from './insurance-details';
import { IRecipientPreferences } from './recipient-preferences';
import { IHlaData } from './hla-data';
import { IHlaAvoids } from './hla-avoids';
import { IRecipientSearchLog } from '@nkr/models/recipient-search-log';
import { ISingleEpletMmRisk } from './single-eplet-mm-risk';
import { IMortalityCalculations } from './mortality-calculations';
import { IEwtSummary } from './ewt-summary';
import { IEwtCounts } from './ewt-counts';
import { IFwtSummary } from '.';

export interface IRecipient {
    id: number;
    isDeleted: boolean;
    isActive: boolean;
    isExportable: boolean;
    alias: string;
    firstName: string,
    lastName: string,
    gender: string;
    race: string;
    height: number;
    weight: number;
    bloodType: string;
    reasonForKidneyFailure: string;
    isWillingToTravel: boolean;
    birthDate: string;
    // emailAddress: string;
    exportableDaysInNkr: number;
    isInChip: boolean;
    isAdpChip: boolean;
    isCompatiblePair: boolean;
    swapType: number;
    position: number;
    statusId: number;
    matchStatusId: number;
    statusDesc: string;
    surgeonContactId: number;
    surgeonContact: string;
    transplantDate: string;
    logisticsProvider: string;
    logisticsProviderName: string;
    courierJobNumber: string;
    centerId: number;
    centerAlias: string,
    hasCheekSwabResults: boolean,
    cpra4: number;
    storedCpra4: number;
    storedPrePrefMatches: number,
    storedPostPrefMatches: number,
    storedPostPrefMatchesC: number;
    storedExportableCount: number;
    clusterId: number;
    age: string;
    estimatedAge: number;
    screeningComments: string;
    center: ICenter;
    adpDonor: IDonor;
    preselectDonors: IDonor[];
    insurance: IInsuranceDetails;
    preferences: IRecipientPreferences;
    hlaData: IHlaData;
    hlaAvoids: IHlaAvoids;
    hasAdpDonorDonated: boolean;
    monthsWaiting: number;
    storedPmpC: number;
    storedPmp: number,
    pairMatchPower: number
    nkrWaitMonths: number;
    unosWaitMonths: number;
    micrositeActive: boolean;
    expectedWaitTimeLowEmm: number;
    crossReferenceHint: string;
    administrativeComments: string;
    comments: string;
    patientType: string;
    adpStatus: number;
    pairCoordinatorId: number;
    pairStatus: number,

    patientTypeId: number;
    patientTypeAbbreviation: string;

    isOnDialysis: boolean;

    //rs
    phiAddress: string,
    phiAddress2: string,
    phiCity: string,
    phiState: string,
    phiPostalCode: string,
    phiPhone: string,
    phiDOB: string,

    adpFirstName: string,
    adpLastName: string,
    adpAddress: string,
    adpCity: string,
    adpState: string,
    adpPostalCode: string,
    adpPhonePrimary: string,
    adpPhoneSecondary: string,
    adpEmailPrimary: string,
    adpEmailSecondary: string

    declineRatio: number,
    declineRatioCurrent: number,
    declineRatioHistoric: number,
    highestDeclineRatio: number,

    warnOnHighDeclineRatio: boolean,
    registeredDate: string,

    eWait: string,
    kWait: number,
    tWait: number,
    wtVariance: number,
    monthsSince: number,
    voucherStatus: string,
    aboCombo: string,
    requireEpletId: number,
    requireEplet: string,
    cmvPreference: string,
    cmvPreferenceId: number,
    ebvPreference: string,
    ebvPreferenceId: number,
    dialysisMonths: string,
    ewtSummary: IEwtSummary;
    initialCount: IEwtCounts;
    priority: string,
    priorityReasons: string[],
    voucherCategories: { category: number, section: string, description: string, policy: string }[],

    dateRegistered: string,
    dateCreatedOn: string,
    dateUpdated: string,
    sendComplianceEmails: boolean,
    targetedChainEnd: boolean,
    // chainEndTargetPriority: number,
    chainNumber: number,
    clusterNumber: number,
    wasDesensitized: boolean,

    mrn: string,
    inactiveReason: number,
    isSwapSaver: boolean,
    isRealTimeSwapFailure: boolean,
    firstExportableDate: string,
    complianceIssues: string[],
    logs: IRecipientSearchLog[],

    donors: IDonor[],
    directedDonors: IDonor[],
    donorBloodTypes: string,
    isChainEnd: boolean,
    histogeneticsRawTypingFile: string,

    labLastReviewedDate: string,

    pairedDonors: IDonor[],

    hasMortalityRisk: boolean,
    // riskPercent: number,
    compatiblePairKPDReasons: number[],
    compatiblePairOtherReason: string,
    hasMedicareAb: boolean,
    intendedDonors: IDonor[],
    recipientShieldEnrolled: boolean,
    centerHasRecipientShield: boolean,
    canSetVoucherActive: boolean,
    coldIschemicHours: number,
    desensitizationType: string,
    primaryNonFunction: boolean,
    donatedBy: number,
    transportationCost: number,
    charterVendor: string,
    shippedMethod: number,
    courierInvoiceNumber: string,
    isKflMatch: false,
    isWillingToMeetDonor: string,
    isSharingGfAndCreatinine: string,
    unosCPRA: number,
    numberOfExportableDaysInNkr: number,
    wasPreviouslySensitized: boolean,
    deathDate: string,
    isAlive: boolean,
    primaryCauseOfDeath: string,
    secondaryCauseOfDeath: string,
    rejectionEpisodes: false,
    rejectionEpisodeDetail: string,
    lostToFollowup: false,
    admitCreatinine: number
    admitCreatinineDate: string,
    creatinineWeek: number,
    creatinineWeekDate: string,
    creatinineMonth: number,
    creatinineMonthDate: string,
    creatinine6Month: string,
    creatinine6MonthDate: string,
    creatinineYear: string,
    creatinineYearDate: string,
    creatinineYear2: string,
    creatinineYear2Date: string,
    creatinineYear3: string,
    creatinineYear3Date: string,
    creatinineYear4: string,
    creatinineYear4Date: string,
    creatinineYear5: string,
    creatinineYear5Date: string,
    creatinineYear6: string,
    creatinineYear6Date: string,
    creatinineYear7: string,
    creatinineYear7Date: string,
    creatinineYear8: string,
    creatinineYear8Date: string,
    creatinineYear9: string,
    creatinineYear9Date: string,
    creatinineYear10: string,
    creatinineYear10Date: string,
    hadDelayedGraftFunction: boolean,
    graftSurvival: boolean,
    graftFailureDate: string,
    graftFailureReason: number,
    donorsUnosId: number,
    desensitization1: boolean,
    desensitization2: boolean,
    desensitization3: boolean
    kflFollowupOptOut: boolean,
    hasDeNovoDSA: boolean,
    deNovoDSADate: string,
    deNovoDSA: string,
    deNovoDSAMFI: number,
    isVoucherHolder: boolean,
    email: string,
    phoneNumber: string,
    fwt: number,
    fwtString: string;
    forecastedWaitTime: string,
    estimatedWaitTimeRaw: string,
    mortalityRiskPercent: number,
    fWait: number,
    mortalityCalculations: IMortalityCalculations,
    fwtSummaryData: IFwtSummary,
    isHardToMatch: boolean,
    isPairedRecipient: boolean,
    donorsNeeded: number,
    donorCompatibleAbo: number,
    aboAndAgeCompatibleDonors: number,
    waitEstimate: string,
    declineWait: string,
    declinePercent: number,
    pendingMatchHistoryDonorId: number
}

export interface IRecipientForList {
    id: number,
    alias: string,
    gender: string,
    age: number,
    bloodType: string,
    centerId: number,
    centerAlias: string,
    donorBloodTypes: string,
    has3Typing: boolean,
    patientTypeId: number,
    patientType: string,
    patientTypeAbbr: string,
    registeredDate: string,
    avoids: number,
    cpra4: number,
    pairMatchPower: number,
    declineRatio: number,
    highestDeclineRatio: number,
    oneYearMortalityRisk: number,
    estimatedWaitTime: number,
    actualWaitTime: number,
    voucherWaitTime: number,
    applicableWaitTime: number,
    waitTimeVariance: number,
    transplantDate: string,
    matchStatusId: number,
    matchStatus: string,
    isExportable: boolean,
    isActive: boolean,
    micrositeRecipientId: number,
    pendingBioMatchDonors: number,
    hasMedicareAb: boolean,
    recipientShieldEnrolled: boolean,
    centerHasRecipientShield: boolean,
    birthDate: string,
    directedDonors: IDonor[],
    isInChip: boolean,
    graftSurvival: boolean,
    numberOfExportableDaysInNkr: number,
    voucherDonorId: number,
    voucherDonorAlias: string,
    voucherDonorCenterId: number,
    canOrderCheekSwab: boolean,
    canDelete: boolean,
    recipientFwt: number,
    recipientFwtString: string,
    isWaitlistEligible: boolean,
    dialysisTime: number,
    isHardToMatch: boolean,
    isVoucherHolder: boolean,
    isPairedPatient: boolean,
    donorsNeeded: number,
    declineRatioHistoric: number,
    donorCompatibleAbo: number,
    aboAndAgeCompatibleDonors: number
    voucherCategories: { category: number, section: string, description: string, policy: string }[],
    requisitionId: number,
    aboCombo: string
}

export interface IRecipientForSearch {
    id: number,
    dateCreatedOn: string,
    dateUpdated: string,
    birthDate: string,
    isDeleted: boolean,
    alias: string,
    gender: string,
    bloodType: string,
    statusId: number,
    status: string,
    centerId: number,
    centerAlias: string,
    isExportable: boolean,
    isActive: boolean,
    // targetedChainEnd: boolean,
    sendComplianceEmails: boolean,
    mrn: string,
    patientTypeId: number,
    inactiveReason: number,
    isSwapSaver: boolean,
    isRealTimeSwapFailure: boolean,
    firstExportableDate: string,
    logs: IRecipientSearchLog[],
    complianceIssues: string[],
    age: number
}

export interface IRecipientWithPairedDonor extends IRecipient {
    currentDonorId: number,
    epletMMRisk: string,
    pairedDonorId: number,
    pairedDonorAlias: string,
    pairedDonorAbo: string,
    pairedDonorType: string
}

export interface IRecipientForSidebar {
    id: number,
    centerId: number,
    alias: string,
    firstName: string,
    lastName: string,
    email: string,
    mrn: string,
    patientTypeId: number,
    isSwapSaver: boolean,
    isRealTimeSwapFailure: boolean,
    statusId: number,
    dateRegistered: string,
    firstExportableDate: string,
    transplantDate: string,
    bloodType: string,
    isExportable: boolean,
    isActive: boolean,
    inactiveReason: number,
    isDeleted: boolean,
    sendComplianceEmails: boolean,
    gender: string,
    birthDate: string,
    chainNumber: number,
    clusterNumber: number,
    position: number,
    hasMedicareAb: boolean,
    centerHasRecipientShield: boolean,
    recipientShieldEnrolled: boolean,
    voucherDonorId: number,
    voucherDonorAlias: string,
    voucherDonorCenterId: number,
    canOrderCheekSwab: boolean,
    canDelete: boolean,
    isWaitlistEligible: boolean,
    requisitionId: number
}

export interface ITransplantedRecipient extends IRecipientForList {
    bmtMm: number,
    singleEpletMmRisk: ISingleEpletMmRisk,
    actualDonorBloodType: string,
    actualDonorAge: number
}